// src/pages/admin/AdminOverview.js
// Superadmin landing page: organization counts by status and the latest sign-ups.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminAPI } from '../../utils/api';
import { STATUS_LABEL, errorOf, fmtDate } from './common';

export default function AdminOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    adminAPI.stats().then(r => setData(r.data)).catch(err => toast.error(errorOf(err, 'Could not load stats')));
  }, []);

  const s = data?.stats;
  const cards = s ? [
    { label: 'Awaiting approval', value: s.awaiting_approval, color: '#f4a118', icon: '⏳', to: 'pending', hint: s.awaiting_verification ? `+${s.awaiting_verification} not verified yet` : null },
    { label: 'Active', value: s.active, color: '#0a6640', icon: '✓', to: 'active' },
    { label: 'Suspended', value: s.suspended, color: '#b91c1c', icon: '⛔', to: 'suspended' },
    { label: 'Deactivated', value: s.deactivated, color: '#4a5568', icon: '⏸', to: 'deactivated' },
    { label: 'Rejected', value: s.rejected, color: '#b4441c', icon: '✕', to: 'rejected' },
    { label: 'Total organizations', value: s.total, color: '#0f766e', icon: '🏢', to: 'all', hint: `${s.signups_7d} new this week · ${s.signups_30d} this month` },
    { label: 'Total users', value: s.users, color: '#0f766e', icon: '👥', path: '/admin/users?status=all' },
  ] : [];

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Admin overview</div>
          <div className="page-subtitle">Organizations and registrations across Post Calendar</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/admin/organizations?status=pending')}>Review pending</button>
      </div>
      <div style={{ padding: 16 }}>
        {!data ? <div className="loading-center"><div className="spinner" /></div> : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
              {cards.map(c => (
                <div key={c.label} className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                  onClick={() => navigate(c.path || `/admin/organizations?status=${c.to}`)}>
                  <span style={{ fontSize: 22, color: c.color }}>{c.icon}</span>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1f2e', lineHeight: 1 }}>{c.value}</div>
                    <div style={{ fontSize: 11, color: '#8892a4', marginTop: 2 }}>{c.label}</div>
                    {c.hint && <div style={{ fontSize: 10, color: '#aab2c0', marginTop: 2 }}>{c.hint}</div>}
                  </div>
                </div>
              ))}
            </div>
            <div className="card">
              <div className="card-header">
                <span className="card-title">Latest registrations</span>
                <button className="btn btn-ghost" onClick={() => navigate('/admin/organizations?status=all')}>View all ›</button>
              </div>
              {data.recent.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#aaa', fontSize: 12 }}>No organizations have registered yet</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead><tr><th style={{ width: 'auto' }}>Organization</th><th>Status</th><th>Registered</th></tr></thead>
                    <tbody>
                      {data.recent.map(o => (
                        <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/organizations?status=${o.status}&org=${o.id}`)}>
                          <td style={{ width: 'auto' }}>
                            <div style={{ fontWeight: 600 }}>{o.name}</div>
                            <div style={{ color: '#8892a4', fontSize: 11 }}>{o.owner_name} · {o.owner_email}</div>
                          </td>
                          <td>
                            <span className={`badge badge-${o.status}`}>{STATUS_LABEL[o.status] || o.status}</span>
                            {o.status === 'pending' && !o.owner_email_verified_at && <span className="badge badge-unverified" style={{ marginLeft: 4 }}>Email unverified</span>}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(o.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
